import { forwardRef, useLayoutEffect, useRef, useState } from 'react';

import type { InputProps } from '@/shared/design-system';
import { Input } from '@/shared/design-system';

import { PasswordInputGroup } from './auth-controls.styled';
import { PasswordRevealToggle } from './password-reveal-toggle';

type PasswordInputProps = Omit<InputProps, 'endControl' | 'endIcon' | 'size' | 'type'>;

type InputSelection = {
  direction: 'backward' | 'forward' | 'none';
  end: number;
  start: number;
};

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>((props, ref) => {
  const { label, 'aria-label': ariaLabel, ...inputProps } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const selectionRef = useRef<InputSelection | null>(null);
  const [revealed, setRevealed] = useState(false);

  useLayoutEffect(() => {
    const input = inputRef.current;
    const selection = selectionRef.current;

    if (!input || !selection) {
      return;
    }

    input.focus();
    input.setSelectionRange(selection.start, selection.end, selection.direction);
    selectionRef.current = null;
  }, [revealed]);

  const toggleReveal = (nextRevealed: boolean) => {
    const input = inputRef.current;

    selectionRef.current = input
      ? {
          start: input.selectionStart ?? 0,
          end: input.selectionEnd ?? 0,
          direction: input.selectionDirection ?? 'none',
        }
      : null;
    setRevealed(nextRevealed);
  };

  const accessibleLabel = ariaLabel ?? (typeof label === 'string' ? label : undefined);

  return (
    <PasswordInputGroup>
      <legend>{accessibleLabel}</legend>
      <Input
        {...inputProps}
        ref={(element) => {
          inputRef.current = element;
          if (typeof ref === 'function') ref(element);
          else if (ref) ref.current = element;
        }}
        label={label}
        aria-label={accessibleLabel}
        size="xl"
        type={revealed ? 'text' : 'password'}
        endControl={
          <PasswordRevealToggle
            revealed={revealed}
            disabled={inputProps.disabled}
            onToggle={toggleReveal}
          />
        }
      />
    </PasswordInputGroup>
  );
});

PasswordInput.displayName = 'PasswordInput';
